import React, { useState, useRef, useEffect, useImperativeHandle } from 'react';
import * as Popover from '@radix-ui/react-popover';
import { DayPicker } from 'react-day-picker';
import { ptBR } from 'date-fns/locale';
import 'react-day-picker/dist/style.css';
import * as S from './styles';

const isUserInSaoPauloTimezone = () => {
  const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const brasiliaTimezones = [
    'America/Sao_Paulo',
    'America/Bahia',
    'America/Fortaleza',
    'America/Recife',
    'America/Belem',
    'America/Araguaina',
    'America/Maceio',
  ];
  return brasiliaTimezones.includes(userTimezone);
};

const InputDateTime = React.forwardRef(
  (
    {
      name,
      value,
      placeholder = 'Selecione data e hora',
      label,
      error,
      disabled = false,
      onChange,
      styleContainer,
      minDate,
      maxDate,
      required = true,
      ...rest
    },
    ref,
  ) => {
    const [isOpen, setIsOpen] = useState(false);
    const [selectedDate, setSelectedDate] = useState(null);
    const [selectedTime, setSelectedTime] = useState({ hours: '', minutes: '' });
    const [displayValue, setDisplayValue] = useState('');
    const [isInSaoPauloTimezone, setIsInSaoPauloTimezone] = useState(true);
    const inputRef = useRef(null);

    useImperativeHandle(
      ref,
      () => {
        return {
          focus() {
            inputRef.current.focus();
          },
          scrollIntoView() {
            inputRef.current.scrollIntoView();
          },
        };
      },
      [],
    );

    useEffect(() => {
      const isSaoPaulo = isUserInSaoPauloTimezone();
      setIsInSaoPauloTimezone(isSaoPaulo);
    }, []);

    // Parse initial value
    useEffect(() => {
      if (value) {
        const date = new Date(value);
        if (!isNaN(date.getTime())) {
          setSelectedDate(date);
          setSelectedTime({
            hours: String(date.getHours()).padStart(2, '0'),
            minutes: String(date.getMinutes()).padStart(2, '0'),
          });
          updateDisplayValue(date);
        }
      } else {
        setSelectedDate(null);
        setSelectedTime({ hours: '00', minutes: '00' });
        setDisplayValue('');
      }
    }, [value]);

    const updateDisplayValue = date => {
      if (date && !isNaN(date.getTime())) {
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');

        setDisplayValue(`${day}/${month}/${year} ${hours}:${minutes}`);
      } else {
        setDisplayValue('');
      }
    };

    const handleTimeInputChange = (field, value) => {
      if (disabled) return;

      if (value === '') {
        setSelectedTime(prev => ({
          ...prev,
          [field]: '',
        }));
        return;
      }

      const numValue = parseInt(value, 10);

      if (field === 'hours') {
        if (!isNaN(numValue) && numValue >= 0 && numValue <= 23) {
          setSelectedTime(prev => ({
            ...prev,
            [field]: value,
          }));
        }
      } else if (field === 'minutes') {
        if (!isNaN(numValue) && numValue >= 0 && numValue <= 59) {
          setSelectedTime(prev => ({
            ...prev,
            [field]: value,
          }));
        }
      }
    };

    const handleTimeInputBlur = field => {
      if (disabled) return;

      const value = selectedTime[field];
      if (value === '') {
        setSelectedTime(prev => ({
          ...prev,
          [field]: '00',
        }));
      } else {
        const numValue = parseInt(value, 10);
        setSelectedTime(prev => ({
          ...prev,
          [field]: String(numValue).padStart(2, '0'),
        }));
      }
    };

    const handleDaySelect = day => {
      if (disabled) return;
      setSelectedDate(day);
    };

    const handleApply = () => {
      if (disabled) return;

      if (selectedDate) {
        const hours = parseInt(selectedTime.hours || '0', 10);
        const minutes = parseInt(selectedTime.minutes || '0', 10);

        const newDate = new Date(selectedDate);
        newDate.setHours(hours, minutes, 0, 0);

        updateDisplayValue(newDate);

        if (onChange) {
          onChange(newDate);
        }
      } else {
        setDisplayValue('');
        if (onChange) {
          onChange(null);
        }
      }

      setIsOpen(false);
    };

    const handleCancel = () => {
      // Reset to current value
      if (value) {
        const date = new Date(value);
        if (!isNaN(date.getTime())) {
          setSelectedDate(date);
          setSelectedTime({
            hours: String(date.getHours()).padStart(2, '0'),
            minutes: String(date.getMinutes()).padStart(2, '0'),
          });
        }
      } else {
        setSelectedDate(null);
        setSelectedTime({ hours: '00', minutes: '00' });
      }

      setIsOpen(false);
    };

    const handleInputClick = () => {
      if (!disabled) {
        setIsOpen(true);
      }
    };

    const handleKeyDown = e => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        handleInputClick();
      }
    };

    return (
      <S.InputDateTime disabled={disabled} style={styleContainer}>
        {label && <S.Label htmlFor={name}>{label}</S.Label>}

        <S.InputContainer>
          <Popover.Root open={isOpen} onOpenChange={setIsOpen}>
            <Popover.Trigger asChild>
              <S.Input
                ref={inputRef}
                id={name}
                name={name}
                value={displayValue || undefined}
                type="text"
                placeholder={placeholder}
                disabled={disabled}
                readOnly
                onClick={handleInputClick}
                onKeyDown={handleKeyDown}
                required={required}
                {...rest}
              />
            </Popover.Trigger>

            <Popover.Portal>
              <S.PopoverContent align="start" sideOffset={4}>
                <S.TimeSection>
                  <S.TimeLabel>Horário</S.TimeLabel>
                  <S.TimeContainer>
                    <S.TimeInput
                      type="number"
                      min="0"
                      max="23"
                      value={selectedTime.hours}
                      onChange={e => handleTimeInputChange('hours', e.target.value)}
                      onBlur={() => handleTimeInputBlur('hours')}
                      placeholder="00"
                      disabled={disabled}
                    />
                    <S.TimeSeparator>:</S.TimeSeparator>
                    <S.TimeInput
                      type="number"
                      min="0"
                      max="59"
                      value={selectedTime.minutes}
                      onChange={e => handleTimeInputChange('minutes', e.target.value)}
                      onBlur={() => handleTimeInputBlur('minutes')}
                      placeholder="00"
                      disabled={disabled}
                    />
                  </S.TimeContainer>
                </S.TimeSection>

                <S.CalendarSection>
                  <DayPicker
                    mode="single"
                    selected={selectedDate}
                    onSelect={handleDaySelect}
                    showOutsideDays
                    captionLayout="dropdown"
                    disabled={[{ before: minDate }, { after: maxDate }]}
                    locale={ptBR}
                  />
                  {!isInSaoPauloTimezone && (
                    <S.WarningText>
                      <i className="material-icons">info</i> Informe a data e hora conforme o seu
                      horário local.
                    </S.WarningText>
                  )}
                </S.CalendarSection>

                <S.ButtonSection>
                  <S.Button variant="cancel" onClick={handleCancel} disabled={disabled}>
                    Cancelar
                  </S.Button>
                  <S.Button onClick={handleApply} disabled={disabled}>
                    Aplicar
                  </S.Button>
                </S.ButtonSection>
              </S.PopoverContent>
            </Popover.Portal>
          </Popover.Root>

          {error && <S.Error>{error}</S.Error>}
        </S.InputContainer>
      </S.InputDateTime>
    );
  },
);

InputDateTime.displayName = 'InputDateTime';

export default InputDateTime;
